import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "../style/installmentpage.css";
import { PageHeader, SearchInput, EmptyState } from "../components/admin/AdminUI";
import { formatDmy, UNKNOWN_PAID_DATE } from "../utils/dateFormat";

const formatRupiah = (n) =>
  typeof n === "number" ? `Rp${n.toLocaleString("id-ID")}` : "-";

// Tanggal bayar per kelas (classList[].paidAt). Pembayaran sebelum fitur ini
// ada tidak punya paidAt, jadi tampil "-".
const formatPaidAt = (iso) => formatDmy(iso) || "-";

const statusColor = {
  Paid: "#16a34a",
  "Next Bill": "#fb3104",
  "Awaiting Payment": "#d97706",
  Upcoming: "#888",
  Free: "#0ea5e9",
  "Covered by CEA": "#3a45c9",
};

const payerBadge = {
  Student: { bg: "#eef1ff", color: "#3a45c9" },
  PIC: { bg: "#fff0ee", color: "#fb3104" },
  "Other Email": { bg: "#f0fdf4", color: "#16a34a" },
};

// Badge status pembayaran satu porsi (payer tertentu untuk 1 kelas).
const billingBadge = (status) => {
  if (status === "paid") return { bg: "#f0fdf4", color: "#16a34a", text: "Paid" };
  if (status === "awaiting_payment") return { bg: "#fff8e1", color: "#d97706", text: "Awaiting" };
  if (status === "payment_failed") return { bg: "#fef2f2", color: "#dc2626", text: "Failed" };
  return { bg: "#f3f4f6", color: "#666", text: "Not billed" };
};

// Backend mengembalikan emailFailures kalau link sudah dibuat tapi emailnya
// gagal terkirim — tampilkan sebagai peringatan, bukan sukses.
const resultIcon = (res) => (res?.data?.emailFailures?.length ? "warning" : "success");

const escapeHtml = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;");

const InstallmentPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [errorMe, setErrorMe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all"); // all | student | pic
  const [filterClass, setFilterClass] = useState("all");
  const [search, setSearch] = useState("");
  const [billingKey, setBillingKey] = useState(null);
  const [scheduleRow, setScheduleRow] = useState(null);
  const [billingClass, setBillingClass] = useState(null);
  const [resendingKey, setResendingKey] = useState(null); // `${row.key}|${classCode}`
  const [payerDetails, setPayerDetails] = useState(null); // grup PIC/Other yg dibuka
  // Pembayaran yang masuk tapi tidak bisa dikreditkan otomatis (dobel / tidak
  // cocok ke tagihan mana pun) — lihat backend utils/paymentOrders.js.
  const [attention, setAttention] = useState([]);
  const [checkingKey, setCheckingKey] = useState(null);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch {
      setErrorMe(true);
    }
  };

  // Gabungkan student reguler (payer = dirinya) + payer CEA To Church
  // (Student / PIC / Other) jadi satu daftar baris seragam.
  // silent = refresh otomatis di latar belakang (tanpa layar "Loading…"). Kalau
  // gagal, data lama dibiarkan apa adanya.
  const fetchAll = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const failed = { data: null };
      const [regRes, churchRes, attRes] = await Promise.all([
        axios.get("/studentinstallments").catch(() => (silent ? failed : { data: [] })),
        axios.get("/church-installment-payers").catch(() => (silent ? failed : { data: [] })),
        axios.get("/payment-orders/attention").catch(() => ({ data: null })),
      ]);
      if (Array.isArray(attRes.data)) setAttention(attRes.data);
      if (regRes.data === null || churchRes.data === null) return;

      const regular = regRes.data.map((s) => ({
        key: `reg-${s.email}`,
        source: "regular",
        payerType: "Student",
        studentId: s.studentId,
        studentName: s.name,
        studentEmail: s.email,
        program: s.program,
        payerName: s.name,
        payerEmail: s.email,
        percent: 100,
        paidCount: s.paidCount,
        totalBillable: s.totalBillable,
        nextClass: s.nextClass,
        nextClassName: s.nextClassName,
        amount: s.nextAmount,
        billingStatus: s.paymentStatus === "awaiting_payment" ? "awaiting_payment" : "ready",
        // Kelas yang link-nya sedang menunggu bayar (dipakai tombol Resend Link).
        billedClassCode: s.billedClassCode,
        // true = student ini sedang re-registration (ada baris Approval menunggu).
        // Selama itu, tombol Bill Next Class dinonaktifkan — admin harus approve/
        // reject dulu di Approval Page (backend juga menolak bill-nya).
        hasPendingApproval: s.hasPendingApproval,
        schedule: s.schedule || [],
      }));

      const church = churchRes.data.map((r) => ({
        key: `chu-${r.registrationId}-${r.studentEmail}-${r.payerType}-${r.payerEmail}`,
        source: "church",
        registrationId: r.registrationId,
        churchName: r.churchName,
        payerType: r.payerType,
        studentId: r.studentId,
        studentName: r.studentName,
        studentEmail: r.studentEmail,
        program: r.program,
        payerName: r.payerName,
        payerEmail: r.payerEmail,
        percent: r.percent,
        paidCount: r.paidCount,
        totalBillable: r.totalBillable,
        nextClass: r.nextClass,
        nextClassName: r.nextClassName,
        amount: r.amount,
        billingStatus: r.billingStatus, // paid | awaiting_payment | payment_failed | not_billed
        studentBillingStatus: r.studentBillingStatus, // status porsi student itu sendiri
        // true = CEA menanggung 100% → tidak ada yang ditagih, hanya ditampilkan.
        ceaCovered: !!r.ceaCovered,
        schedule: r.schedule || [],
      }));

      setRows([...regular, ...church]);
    } catch {
      if (!silent) setRows([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin({ pathname: "/installmentpage" });
    getMe();
    fetchAll();
  }, []);

  // Status Paid/Unpaid diperbarui server begitu Midtrans mengirim notifikasi
  // pembayaran. Halaman ini ikut menyegarkan diri tiap 30 detik (dan saat tab
  // dibuka lagi) supaya admin langsung melihatnya tanpa reload.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") fetchAll(true);
    };
    const timer = setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  // 409 = backend sengaja tidak membuat link baru (link sebelumnya ternyata
  // sudah dibayar, atau Midtrans belum bisa memastikan) — bukan error biasa.
  const failIcon = (error) => (error.response?.status === 409 ? "info" : "error");

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account!",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  // Bill kelas berikutnya untuk 1 baris (regular = student; church = SEMUA payer).
  // Regular: replacePending → kalau masih ada link Awaiting Payment, backend
  // membatalkannya dan mengirim link baru (link lama tidak berlaku lagi).
  const billRow = async (row, classCode = null) => {
    try {
      if (row.source === "regular") {
        return await axios.post(`/student/${row.studentEmail}/bill-installment`, {
          ...(classCode ? { classCode } : {}),
          replacePending: true,
        });
      }
      return await axios.post("/churchregistration/bill-next-class", {
        registrationId: row.registrationId,
        studentEmail: row.studentEmail,
        ...(classCode ? { classCode } : {}),
      });
    } catch (err) {
      throw err;
    }
  };

  const handleBillNext = async (row) => {
    const who = row.source === "church" ? `all payers of ${row.studentName}` : row.studentName;
    const result = await Swal.fire({
      text: `Send a payment link to ${who} for ${row.nextClassName}?`,
      icon: "question",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, send link",
    });
    if (!result.isConfirmed) return;

    setBillingKey(row.key);
    try {
      const res = await billRow(row);
      setPayerDetails(null);
      await Swal.fire({
        text: res.data.msg || "Payment link sent.",
        icon: resultIcon(res),
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchAll();
    } catch (error) {
      if (error.response?.status === 409) fetchAll(true);
      Swal.fire({
        text: error.response?.data?.msg || "Failed to send the payment link.",
        icon: failIcon(error),
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
    } finally {
      setBillingKey(null);
    }
  };

  // Kirim link untuk SATU kelas tertentu (dari View Schedule).
  const handleBillClass = async (row, scheduleItem) => {
    // Tutup overlay jadwal dulu supaya dialog konfirmasi tampil bersih di atasnya.
    setScheduleRow(null);
    const who = row.source === "church" ? `all payers of ${row.studentName}` : row.studentName;
    // Student regular hanya punya satu link aktif — link yang masih menunggu
    // bayar (kelas lain) akan dibatalkan dan diganti link ini.
    const pendingCode =
      row.source === "regular" && row.billingStatus === "awaiting_payment" ? row.billedClassCode : null;
    const replaceNote = pendingCode
      ? ` The pending payment link for ${pendingCode} will be cancelled and will no longer work.`
      : "";
    const result = await Swal.fire({
      text: `Send a payment link to ${who} for ${scheduleItem.code} (${scheduleItem.label})?${replaceNote}`,
      icon: "question",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, send link",
    });
    if (!result.isConfirmed) return;

    setBillingClass(scheduleItem.code);
    try {
      const res = await billRow(row, scheduleItem.code);
      setScheduleRow(null);
      setPayerDetails(null);
      await Swal.fire({
        text: res.data.msg || `Payment link sent for ${scheduleItem.code}.`,
        icon: resultIcon(res),
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchAll();
    } catch (error) {
      if (error.response?.status === 409) fetchAll(true);
      Swal.fire({
        text: error.response?.data?.msg || "Failed to send the payment link.",
        icon: failIcon(error),
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
    } finally {
      setBillingClass(null);
    }
  };

  // Kirim ULANG link yang masih "Awaiting Payment" (mis. email pertama tidak
  // sampai). Backend membatalkan link lama di Midtrans lalu mengirim link baru,
  // jadi link di email sebelumnya langsung tidak berlaku.
  // classCode: kelas yang sedang ditagih (regular = billedClassCode, church =
  // kelas yang statusnya Awaiting Payment).
  const handleResend = async (row, classCode) => {
    if (!classCode) return;
    const className = (row.schedule || []).find((s) => s.code === classCode)?.label;
    const classText = className ? `${classCode} (${className})` : classCode;
    const who =
      row.source === "church"
        ? `every payer of ${row.studentName} who hasn't paid ${classText} yet`
        : `${row.studentName} (${row.studentEmail}) for ${classText}`;

    setScheduleRow(null);
    const result = await Swal.fire({
      html:
        `Send a <strong>new</strong> payment link to ${who.replace(/&/g, "&amp;").replace(/</g, "&lt;")}?<br/><br/>` +
        `The previous link will be cancelled and will no longer work.`,
      icon: "question",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, resend link",
    });
    if (!result.isConfirmed) return;

    setResendingKey(`${row.key}|${classCode}`);
    try {
      const res =
        row.source === "regular"
          ? await axios.post(`/student/${row.studentEmail}/resend-installment-link`, { classCode })
          : await axios.post("/churchregistration/resend-class-link", {
              registrationId: row.registrationId,
              studentEmail: row.studentEmail,
              classCode,
            });
      await Swal.fire({
        text: res.data.msg || "A new payment link has been sent.",
        icon: resultIcon(res),
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchAll();
    } catch (error) {
      if (error.response?.status === 409) fetchAll(true);
      Swal.fire({
        text: error.response?.data?.msg || "Failed to resend the payment link.",
        icon: failIcon(error),
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
    } finally {
      setResendingKey(null);
    }
  };

  // Tanya Midtrans SEKARANG untuk semua link yang masih terbuka milik student
  // ini (berguna kalau notifikasi pembayaran dari Midtrans terlambat/hilang).
  // Yang sudah dibayar langsung dicatat Paid. Tidak membatalkan link apa pun.
  const handleCheckPayment = async (row) => {
    setScheduleRow(null);
    setCheckingKey(row.key);
    try {
      const res = await axios.post("/payment-orders/check", {
        studentEmail: row.studentEmail,
        ...(row.source === "church" ? { registrationId: row.registrationId } : {}),
      });
      await Swal.fire({
        text: res.data.msg,
        icon: res.data.duplicates ? "warning" : res.data.credited ? "success" : "info",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchAll(true);
    } catch (error) {
      Swal.fire({
        text: error.response?.data?.msg || "Couldn't check the payment right now.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
    } finally {
      setCheckingKey(null);
    }
  };

  const handleResolveAttention = async (item) => {
    const result = await Swal.fire({
      html:
        `Mark this payment as handled?<br/><br/><strong>${escapeHtml(item.studentName || item.payerEmail || item.orderId)}</strong> — ${formatRupiah(item.paidAmount)}<br/>` +
        `<small>Do this after you have refunded it or sorted it out manually.</small>`,
      icon: "question",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, mark as handled",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.post(`/payment-orders/${encodeURIComponent(item.orderId)}/resolve`);
      fetchAll(true);
    } catch (error) {
      Swal.fire({ text: error.response?.data?.msg || "Failed to update.", icon: "error", confirmButtonColor: "#fb3104" });
    }
  };

  // Kelas yang link-nya sedang menunggu bayar untuk baris di tabel utama.
  const awaitingClassOf = (r) => (r.source === "regular" ? r.billedClassCode : r.nextClass);

  // Bill kelas berikutnya untuk SEMUA student di bawah 1 payer (PIC/Other) —
  // satu klik. Tiap student menagih semua payer-nya sesuai porsi.
  const handleBillAllForPayer = async (group) => {
    // Student ikut ditagih kalau porsi payer ini ATAU porsi student-nya belum
    // ditagih / gagal. Backend hanya mengirim link ke payer yang statusnya
    // bukan awaiting_payment/paid, jadi yang masih menunggu tidak dikirimi lagi.
    const canBill = (s) => s === "not_billed" || s === "payment_failed";
    const billable = group.rows.filter((r) => canBill(r.billingStatus) || canBill(r.studentBillingStatus));
    if (billable.length === 0) {
      Swal.fire({
        text: "No students are ready to be billed — every payer is already awaiting payment or has paid for the next class.",
        icon: "info",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
      return;
    }
    const result = await Swal.fire({
      text: `Send the next-class bill for ${billable.length} student(s) under ${group.payerName}? Only payers whose share is not billed yet or failed get a new link — anyone still awaiting payment or already paid is skipped. ${group.payerName}'s share (if due) comes as ONE combined payment link.`,
      icon: "question",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, bill ${billable.length}`,
    });
    if (!result.isConfirmed) return;

    setPayerDetails(null);
    setBillingKey(group.groupKey);
    try {
      const res = await axios.post("/churchregistration/bill-all-next-classes", {
        registrationId: group.registrationId,
      });
      await Swal.fire({
        text: res.data.msg || "Payment links sent.",
        icon: resultIcon(res),
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
      fetchAll();
    } catch (error) {
      Swal.fire({
        text: error.response?.data?.msg || "Failed to send the payment links.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    } finally {
      setBillingKey(null);
    }
  };

  // Student → baris per student. PIC / Other → dikelompokkan jadi 1 baris per
  // payer (nama payer saja), dengan daftar student yang dibayarnya di detail.
  const groupPayers = (list) => {
    const map = new Map();
    for (const r of list.filter((x) => x.payerType !== "Student")) {
      const k = `${r.registrationId}-${r.payerType}-${r.payerEmail}`;
      if (!map.has(k)) {
        map.set(k, {
          groupKey: k,
          payerType: r.payerType,
          payerName: r.payerName,
          payerEmail: r.payerEmail,
          percent: r.percent,
          registrationId: r.registrationId,
          churchName: r.churchName,
          rows: [],
        });
      }
      map.get(k).rows.push(r);
    }
    return [...map.values()].map((g) => ({
      ...g,
      totalAmount: g.rows.reduce((a, x) => a + (x.amount || 0), 0),
    }));
  };

  // ── Search: nama / email (student maupun payer) + Student ID ──
  const q = search.trim().toLowerCase();
  const searched = !q
    ? rows
    : rows.filter((r) =>
        [r.studentName, r.studentEmail, r.studentId, r.payerName, r.payerEmail]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );

  // ── Filter: tab payer + kelas berikutnya ──
  const byTab = searched.filter((r) => {
    if (tab === "student") return r.payerType === "Student" && !r.ceaCovered;
    if (tab === "pic") return r.payerType === "PIC";
    if (tab === "cea") return r.ceaCovered;
    return true; // all
  });
  const classOptions = [...new Set(byTab.map((r) => r.nextClass))].filter(Boolean).sort();
  const visible =
    filterClass === "all" ? byTab : byTab.filter((r) => r.nextClass === filterClass);

  const studentRows = visible.filter((r) => r.payerType === "Student");
  const payerGroups = groupPayers(visible);

  // Angka di tab mengikuti BARIS YANG BENAR-BENAR TAMPIL: student dihitung
  // per student, PIC/Other dihitung per payer (bukan per student yang dibayar).
  const counts = {
    all:
      searched.filter((r) => r.payerType === "Student").length +
      groupPayers(searched).length,
    student: searched.filter((r) => r.payerType === "Student" && !r.ceaCovered).length,
    pic: groupPayers(searched.filter((r) => r.payerType === "PIC")).length,
    cea: searched.filter((r) => r.ceaCovered).length,
  };

  // ── Bill to All: tagih kelas berikutnya untuk SEMUA baris yang tampil ──
  // (mengikuti tab + search + filter kelas). Student reguler ditagih satu per
  // satu; CEA To Church dikelompokkan per registrasi supaya PIC/Other tetap
  // menerima SATU link gabungan. Yang sedang menunggu bayar / pending
  // re-registration dilewati.
  const canBillShare = (s) => s === "not_billed" || s === "payment_failed";
  const bulkRegular = visible.filter(
    (r) => r.source === "regular" && r.nextClass && !r.hasPendingApproval && r.billingStatus !== "awaiting_payment",
  );
  const bulkChurch = new Map(); // registrationId → Set(studentEmail)
  for (const r of visible) {
    if (r.source !== "church" || !r.nextClass) continue;
    if (!(canBillShare(r.billingStatus) || canBillShare(r.studentBillingStatus))) continue;
    if (!bulkChurch.has(r.registrationId)) bulkChurch.set(r.registrationId, new Set());
    bulkChurch.get(r.registrationId).add(r.studentEmail);
  }
  const bulkCount =
    bulkRegular.length + [...bulkChurch.values()].reduce((a, s) => a + s.size, 0);

  const handleBillToAll = async () => {
    if (bulkCount === 0) {
      Swal.fire({
        text: "No students in the current filter are ready to be billed — they are already awaiting payment, have paid, or have a pending re-registration.",
        icon: "info",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
      return;
    }
    const filterParts = [
      `tab: ${TABS.find((t) => t.id === tab)?.label}`,
      `next class: ${filterClass === "all" ? "all" : filterClass}`,
      search ? `search: "${search}"` : null,
    ].filter(Boolean);
    const result = await Swal.fire({
      html: `Send the next-class bill to <strong>${bulkCount}</strong> student(s) matching the current filter (${filterParts.join(", ").replace(/&/g, "&amp;").replace(/</g, "&lt;")})?<br/><br/>` +
        `For CEA To Church, every payer is billed their share and each PIC/Other payer gets ONE combined link. ` +
        `Anyone already awaiting payment or paid is skipped.`,
      icon: "question",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, bill ${bulkCount}`,
    });
    if (!result.isConfirmed) return;

    setBillingKey("__all__");
    let ok = 0;
    const failures = [];
    // Link sudah dibuat (status Awaiting Payment) tapi emailnya gagal terkirim.
    const emailFails = [];
    try {
      for (const r of bulkRegular) {
        try {
          const res = await axios.post(`/student/${r.studentEmail}/bill-installment`, {});
          ok++;
          for (const f of res.data?.emailFailures || []) {
            emailFails.push(`${r.studentName} (${f.to}): ${f.error}`);
          }
        } catch (err) {
          failures.push(`${r.studentName}: ${err.response?.data?.msg || "failed"}`);
        }
      }
      for (const [registrationId, emails] of bulkChurch) {
        try {
          const res = await axios.post("/churchregistration/bill-all-next-classes", {
            registrationId,
            studentEmails: [...emails],
          });
          ok += emails.size;
          // Student yang dilewati karena link lamanya ternyata sudah dibayar /
          // belum bisa dipastikan ke Midtrans — disebut di pesan backend.
          if (/skipped/.test(res.data?.msg || "")) {
            failures.push(`CEA To Church #${registrationId}: ${res.data.msg}`);
          }
          for (const f of res.data?.emailFailures || []) {
            emailFails.push(`CEA To Church #${registrationId} — ${f.to}: ${f.error}`);
          }
        } catch (err) {
          failures.push(`CEA To Church #${registrationId}: ${err.response?.data?.msg || "failed"}`);
        }
      }
      const list = (items) =>
        `<div style="text-align:left;font-size:13px;max-height:200px;overflow:auto;margin-top:6px">${items
          .map((f) => `• ${escapeHtml(f)}`)
          .join("<br/>")}</div>`;
      await Swal.fire({
        html:
          `Bill sent for <strong>${ok}</strong> student(s).` +
          (emailFails.length
            ? `<br/><br/><strong>${emailFails.length} email(s) FAILED to send</strong> — the payment link was created ` +
              `(status Awaiting Payment), but the email did not go out. Open View Schedule and click ` +
              `<strong>Resend Payment Link</strong> for:${list(emailFails)}`
            : "") +
          (failures.length ? `<br/><br/><strong>${failures.length} failed:</strong>${list(failures)}` : ""),
        icon: failures.length || emailFails.length ? (ok ? "warning" : "error") : "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
      fetchAll();
    } finally {
      setBillingKey(null);
    }
  };

  const TABS = [
    { id: "all", label: "All" },
    { id: "student", label: "Student" },
    { id: "pic", label: "PIC" },
    { id: "cea", label: "CEA 100%" },
  ];

  const payerChip = (type, text) => {
    const badge = payerBadge[type] || { bg: "#f3f4f6", color: "#666" };
    return (
      <span className="adm-badge" style={{ backgroundColor: badge.bg, color: badge.color }}>
        {text}
      </span>
    );
  };

  return (
    <div className="adm-main">
      <PageHeader
        title="Installment Payments"
        subtitle="Students paying per class. Bill the next class as its month arrives — for CEA To Church, one class is billed to every payer according to their share."
      />

      {attention.length > 0 && (
        <div
          className="adm-card"
          style={{ border: "1px solid #fca5a5", background: "#fef2f2", padding: "14px 18px", marginBottom: "16px" }}
        >
          <div style={{ fontWeight: 700, color: "#b91c1c", marginBottom: "4px" }}>
            <i className="bi bi-exclamation-triangle-fill" /> {attention.length} payment{attention.length > 1 ? "s" : ""} need{attention.length > 1 ? "" : "s"} your attention
          </div>
          <p className="adm-help" style={{ margin: "0 0 8px" }}>
            Money came in through Midtrans but was not credited automatically — either the class was already paid
            through another link (refund needed), or the payment couldn't be matched to any bill.
          </p>
          {attention.map((a) => (
            <div
              key={a.orderId}
              style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", padding: "8px 0", borderTop: "1px solid #fecaca" }}
            >
              <span className="adm-badge" style={{ backgroundColor: "#fee2e2", color: "#b91c1c" }}>
                {a.status === "duplicate" ? "Double payment" : "Unmatched payment"}
              </span>
              <span style={{ fontWeight: 600 }}>{a.studentName || a.payerEmail || "Unknown payer"}</span>
              {a.studentId && <span className="adm-muted-text">{a.studentId}</span>}
              {a.classCode && <span>{a.classCode}</span>}
              <span style={{ fontWeight: 600 }}>{formatRupiah(a.paidAmount)}</span>
              <span className="adm-muted-text">{formatPaidAt(a.paidAt)} · {a.orderId}</span>
              <button
                type="button"
                className="adm-btn adm-btn--secondary adm-btn--sm ms-auto"
                onClick={() => handleResolveAttention(a)}
              >
                Mark as handled
              </button>
              {a.note && <div className="adm-muted-text" style={{ flexBasis: "100%", fontSize: "13px" }}>{a.note}</div>}
            </div>
          ))}
        </div>
      )}

      <div className="adm-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`adm-tab${tab === t.id ? " adm-tab--active" : ""}`}
            onClick={() => { setTab(t.id); setFilterClass("all"); }}
          >
            {t.label}
            <span className="adm-tab-count">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      <div className="adm-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, or student ID" />
        <select
          className="form-select"
          style={{ width: "auto", minWidth: "170px" }}
          value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}
          aria-label="Next class"
        >
          <option value="all">All next classes</option>
          {classOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {search && (
          <button type="button" className="adm-btn adm-btn--ghost" onClick={() => setSearch("")}>
            Clear search
          </button>
        )}
        <button
          type="button"
          className="adm-btn adm-btn--primary ms-auto"
          onClick={handleBillToAll}
          disabled={isLoading || billingKey === "__all__"}
          title="Bill the next class for every student in the current filter"
        >
          {billingKey === "__all__" ? "Sending..." : `Bill to All (${bulkCount})`}
        </button>
      </div>

      <div className="adm-card adm-card--flush">
        {isLoading ? (
          <EmptyState icon="bi-hourglass" title="Loading installments…" />
        ) : studentRows.length === 0 && payerGroups.length === 0 ? (
          <EmptyState
            icon="bi-credit-card"
            title="Nothing to bill here"
            text={search ? `No results for "${search}".` : "No installment rows to show for this filter."}
          />
        ) : (
          <div className="adm-table-wrap adm-table-wrap--scroll">
            <table className="adm-table" style={{ minWidth: "900px" }}>
              <thead>
                <tr>
                  <th>Student / Payer</th>
                  <th>Payer Type</th>
                  <th>Progress</th>
                  <th>Next Class</th>
                  <th>Amount</th>
                  <th className="adm-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Baris per student (payer = student itu sendiri) */}
                {studentRows.map((r) => (
                  <tr key={r.key}>
                    <td>
                      <div className="adm-cell-title">{r.studentName}</div>
                      <div className="adm-cell-meta">{r.studentId}</div>
                    </td>
                    <td>
                      {r.ceaCovered ? (
                        <span
                          className="adm-badge"
                          style={{ backgroundColor: "#eef1ff", color: "#3a45c9" }}
                          title={r.churchName ? `CEA To Church · ${r.churchName}` : undefined}
                        >
                          Covered by CEA 100%
                        </span>
                      ) : (
                        payerChip(r.payerType, "Student")
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <ProgressBar value={r.paidCount} total={r.totalBillable} />
                    </td>
                    <td>{r.ceaCovered ? <span className="adm-muted-text">—</span> : r.nextClassName}</td>
                    <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{formatRupiah(r.amount)}</td>
                    <td className="adm-col-actions">
                      <div className="adm-actions">
                        <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => setScheduleRow(r)}>
                          View Schedule
                        </button>
                        {r.ceaCovered ? (
                          <span
                            className="adm-status adm-status--info"
                            title="CEA covers 100% of this student's fee — nothing to bill."
                            style={{ cursor: "help" }}
                          >
                            No Payment Needed
                          </span>
                        ) : r.hasPendingApproval ? (
                          <span
                            className="adm-status adm-status--info"
                            title="Student ini sedang re-registration di Approval Page. Approve/reject dulu di sana sebelum menagih kelas berikutnya."
                            style={{ cursor: "help" }}
                          >
                            Pending Re-Registration
                          </span>
                        ) : r.billingStatus === "awaiting_payment" ? (
                          <>
                            <span className="adm-status adm-status--warning">Awaiting Payment</span>
                            {awaitingClassOf(r) && (
                              <button
                                type="button"
                                className="adm-btn adm-btn--secondary adm-btn--sm"
                                onClick={() => handleResend(r, awaitingClassOf(r))}
                                disabled={resendingKey === `${r.key}|${awaitingClassOf(r)}`}
                                title="Send a new payment link — the previous link will stop working"
                              >
                                {resendingKey === `${r.key}|${awaitingClassOf(r)}` ? "Sending..." : "Resend Link"}
                              </button>
                            )}
                            <button
                              type="button"
                              className="adm-btn adm-btn--ghost adm-btn--sm"
                              onClick={() => handleCheckPayment(r)}
                              disabled={checkingKey === r.key}
                              title="Ask Midtrans now whether this student has paid"
                            >
                              {checkingKey === r.key ? "Checking..." : "Check Payment"}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="adm-btn adm-btn--primary adm-btn--sm"
                            onClick={() => handleBillNext(r)}
                            disabled={billingKey === r.key}
                          >
                            {billingKey === r.key ? "Sending..." : "Bill Next Class"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {/* Baris per PIC / Other — nama payer saja, detail student di modal */}
                {payerGroups.map((g) => (
                  <tr key={g.groupKey}>
                    <td>
                      <div className="adm-cell-title">{g.payerName}</div>
                      <div className="adm-cell-meta">{g.payerEmail}</div>
                    </td>
                    <td>{payerChip(g.payerType, `${g.payerType} · ${g.percent}%`)}</td>
                    <td style={{ whiteSpace: "nowrap", color: "var(--adm-text-2)" }}>
                      Paying for {g.rows.length} student{g.rows.length > 1 ? "s" : ""}
                    </td>
                    <td className="adm-muted-text">—</td>
                    <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{formatRupiah(g.totalAmount)}</td>
                    <td className="adm-col-actions">
                      <button type="button" className="adm-btn adm-btn--dark adm-btn--sm" onClick={() => setPayerDetails(g)}>
                        See Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════ SCHEDULE MODAL — kirim link per kelas ══════════ */}
      {scheduleRow &&
        createPortal(
          <div className="adm-modal-backdrop" onClick={() => setScheduleRow(null)}>
            <div className="adm-modal" style={{ width: "760px" }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-modal-header">
                <div>
                  <h5 className="adm-card-title" style={{ fontSize: "17px" }}>{scheduleRow.studentName} — Installment Schedule</h5>
                  <p className="adm-card-subtitle">
                    {scheduleRow.studentId} · {scheduleRow.program} · {scheduleRow.paidCount}/{scheduleRow.totalBillable} paid
                    {scheduleRow.ceaCovered
                      ? " · covered by CEA 100%"
                      : scheduleRow.source === "church"
                        ? " · billed to all payers"
                        : ""}
                  </p>
                </div>
                <button type="button" className="adm-modal-close" aria-label="Close" onClick={() => setScheduleRow(null)}>
                  ×
                </button>
              </div>

              <div className="adm-table-wrap">
                <table className="adm-table" style={{ minWidth: "600px" }}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Class</th>
                      <th>Status</th>
                      <th>Paid On</th>
                      <th style={{ textAlign: "right" }}>Amount</th>
                      <th className="adm-col-actions">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(scheduleRow.schedule || []).map((item, i) => {
                      const billable =
                        !scheduleRow.ceaCovered &&
                        item.amount != null &&
                        item.status !== "Paid" &&
                        item.status !== "Free" &&
                        item.status !== "Awaiting Payment";
                      return (
                        <tr key={i}>
                          <td>{item.label}</td>
                          <td style={{ fontWeight: 600 }}>{item.code}</td>
                          <td style={{ fontWeight: 700, color: statusColor[item.status] || "var(--adm-text)" }}>{item.status}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {item.paidDateUnknown ? UNKNOWN_PAID_DATE : formatPaidAt(item.paidAt)}
                          </td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            {item.amount != null ? formatRupiah(item.amount) : "-"}
                          </td>
                          <td className="adm-col-actions">
                            {billable && (
                              <button
                                type="button"
                                className="adm-btn adm-btn--primary adm-btn--sm"
                                onClick={() => handleBillClass(scheduleRow, item)}
                                disabled={billingClass === item.code}
                              >
                                {billingClass === item.code ? "Sending..." : "Send Payment Link"}
                              </button>
                            )}
                            {item.status === "Awaiting Payment" && (
                              <button
                                type="button"
                                className="adm-btn adm-btn--secondary adm-btn--sm"
                                onClick={() => handleResend(scheduleRow, item.code)}
                                disabled={resendingKey === `${scheduleRow.key}|${item.code}`}
                                title="Send a new payment link — the previous link will stop working"
                              >
                                {resendingKey === `${scheduleRow.key}|${item.code}` ? "Sending..." : "Resend Payment Link"}
                              </button>
                            )}
                            {item.status === "Awaiting Payment" && (
                              <button
                                type="button"
                                className="adm-btn adm-btn--ghost adm-btn--sm"
                                style={{ marginLeft: "6px" }}
                                onClick={() => handleCheckPayment(scheduleRow)}
                                disabled={checkingKey === scheduleRow.key}
                                title="Ask Midtrans now whether this has been paid"
                              >
                                {checkingKey === scheduleRow.key ? "Checking..." : "Check Payment"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {scheduleRow.ceaCovered ? (
                <p className="adm-help" style={{ padding: "12px 22px 18px", margin: 0 }}>
                  CEA covers <strong>100%</strong> of this student's fee
                  {scheduleRow.churchName ? ` (CEA To Church · ${scheduleRow.churchName})` : ""} — no payment link is ever sent.
                </p>
              ) : scheduleRow.source === "church" && (
                <p className="adm-help" style={{ padding: "12px 22px 18px", margin: 0 }}>
                  Amount shown is <strong>{scheduleRow.payerName}</strong>'s share ({scheduleRow.percent}%) of each class.
                  Sending a link bills every payer their own share of that class.
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* ══════════ PAYER (PIC / Other) DETAILS — student mana saja ══════════ */}
      {payerDetails &&
        createPortal(
          <div className="adm-modal-backdrop" onClick={() => setPayerDetails(null)}>
            <div className="adm-modal" style={{ width: "800px" }} onClick={(e) => e.stopPropagation()}>
              <div className="adm-modal-header">
                <div>
                  <h5 className="adm-card-title" style={{ fontSize: "17px" }}>{payerDetails.payerName}</h5>
                  <p className="adm-card-subtitle">
                    {payerDetails.payerType} · {payerDetails.percent}% · {payerDetails.payerEmail}
                    {payerDetails.churchName ? ` · ${payerDetails.churchName}` : ""}
                  </p>
                </div>
                <button type="button" className="adm-modal-close" aria-label="Close" onClick={() => setPayerDetails(null)}>
                  ×
                </button>
              </div>

              <div className="adm-modal-body d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <p style={{ margin: 0, fontSize: "13.5px", color: "var(--adm-text-2)" }}>
                  Paying for <strong>{payerDetails.rows.length}</strong> student{payerDetails.rows.length > 1 ? "s" : ""} — total
                  next-class share <strong>{formatRupiah(payerDetails.totalAmount)}</strong>.
                </p>
                <button
                  type="button"
                  className="adm-btn adm-btn--primary"
                  onClick={() => handleBillAllForPayer(payerDetails)}
                  disabled={billingKey === payerDetails.groupKey}
                >
                  {billingKey === payerDetails.groupKey ? "Sending..." : "Bill Next Class — All Students"}
                </button>
              </div>

              <div className="adm-table-wrap" style={{ borderTop: "1px solid var(--adm-border-soft)" }}>
                <table className="adm-table" style={{ minWidth: "680px" }}>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Program</th>
                      <th>Next Class</th>
                      <th style={{ textAlign: "right" }}>Share</th>
                      <th>Status</th>
                      <th className="adm-col-actions">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payerDetails.rows.map((r) => {
                      const mine = billingBadge(r.billingStatus);
                      const theirs = billingBadge(r.studentBillingStatus);
                      const chip = (label, b) => (
                        <div className="d-flex align-items-center gap-2" style={{ marginBottom: "3px" }}>
                          <span style={{ fontSize: "11.5px", color: "var(--adm-muted)", width: "58px" }}>{label}</span>
                          <span className="adm-badge" style={{ backgroundColor: b.bg, color: b.color }}>{b.text}</span>
                        </div>
                      );
                      return (
                        <tr key={r.key}>
                          <td>
                            <div className="adm-cell-title">{r.studentName}</div>
                            <div className="adm-cell-meta">{r.studentId}</div>
                          </td>
                          <td style={{ color: "var(--adm-text-2)" }}>{r.program}</td>
                          <td>{r.nextClassName}</td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{formatRupiah(r.amount)}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {chip(`${payerDetails.payerType}:`, mine)}
                            {chip("Student:", theirs)}
                          </td>
                          <td className="adm-col-actions">
                            <button
                              type="button"
                              className="adm-btn adm-btn--secondary adm-btn--sm"
                              onClick={() => { setScheduleRow(r); setPayerDetails(null); }}
                            >
                              View Schedule
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="adm-help" style={{ padding: "12px 22px 18px", margin: 0 }}>
                "Share" is this payer's portion of each student's next class. Billing sends{" "}
                <strong>{payerDetails.payerName}</strong> a single combined link covering all students listed above,
                while each student receives their own link for their own share.
              </p>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// Bar kecil "x / y paid" untuk kolom Progress.
const ProgressBar = ({ value, total }) => {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div style={{ minWidth: "120px" }}>
      <div style={{ fontSize: "12.5px", color: "var(--adm-text-2)", marginBottom: "4px" }}>
        <strong style={{ color: "var(--adm-text)" }}>{value}</strong> / {total} paid
      </div>
      <div className="adm-progress">
        <div className="adm-progress-bar" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default InstallmentPage;
